import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Cloud, CloudRain, Sun, CloudSnow, CloudFog, Zap, Wind, Droplets, Thermometer, Moon, ChevronDown, ChevronUp } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONDITION_ICONS = {
  clear: Sun,
  cloudy: Cloud,
  rainy: CloudRain,
  snowy: CloudSnow,
  foggy: CloudFog,
  stormy: Zap,
};

const CONDITION_GRADIENTS = {
  clear: 'from-amber-400 via-orange-400 to-rose-400',
  cloudy: 'from-slate-400 via-slate-500 to-slate-600',
  rainy: 'from-blue-500 via-blue-600 to-slate-700',
  snowy: 'from-blue-200 via-cyan-300 to-blue-400',
  foggy: 'from-slate-300 via-slate-400 to-slate-500',
  stormy: 'from-purple-600 via-slate-700 to-slate-900',
};

export default function WeatherWidget({ onWeatherChange, effectsEnabled, onToggleEffects }) {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchWeather = async () => {
      try {
        const response = await axios.get(`${API_URL}/weather`);
        if (cancelled) return;
        setWeather(response.data.data);
        if (onWeatherChange) onWeatherChange(response.data.data);
      } catch (error) {
        console.error('Weather fetch error:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchWeather();
    // Refresh every 10 minutes
    const interval = setInterval(fetchWeather, 600000);
    return () => { cancelled = true; clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || !weather) {
    return null;
  }

  const IconComponent = weather.is_day 
    ? (CONDITION_ICONS[weather.condition] || Sun)
    : (weather.condition === 'clear' ? Moon : CONDITION_ICONS[weather.condition] || Cloud);
  
  const gradient = CONDITION_GRADIENTS[weather.condition] || CONDITION_GRADIENTS.clear;
  const isRaining = ['rainy', 'stormy'].includes(weather.condition);

  return (
    <div 
      className="fixed top-24 left-4 z-30 animate-in slide-in-from-left duration-300"
      data-testid="weather-widget"
    >
      <div 
        className={`bg-gradient-to-br ${gradient} backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl overflow-hidden transition-all duration-300 ${expanded ? 'w-72' : 'w-56'}`}
      >
        {/* Compact view */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full p-4 flex items-center justify-between hover:bg-white/10 transition-colors"
          data-testid="weather-toggle-btn"
        >
          <div className="flex items-center gap-3">
            <div className="relative">
              <IconComponent className="w-10 h-10 text-white drop-shadow-lg" strokeWidth={1.5} />
              {isRaining && (
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                  {[...Array(3)].map((_, i) => (
                    <div 
                      key={i}
                      className="w-0.5 h-2 bg-blue-200 rounded-full animate-bounce"
                      style={{ animationDelay: `${i * 0.15}s` }}
                    />
                  ))}
                </div>
              )}
            </div>
            <div className="text-left">
              <div className="text-3xl font-black text-white drop-shadow-lg" data-testid="weather-temp">
                {Math.round(weather.temperature)}°
              </div>
              <div className="text-xs text-white/90 font-medium leading-tight">
                {weather.description}
              </div>
            </div>
          </div>
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-white/70" />
          ) : (
            <ChevronDown className="w-5 h-5 text-white/70" />
          )}
        </button>

        {/* Expanded details */}
        {expanded && (
          <div className="px-4 pb-4 border-t border-white/20 pt-3">
            <div className="text-xs text-white/80 mb-3 flex items-center gap-1">
              📍 {weather.location}
            </div>
            
            <div className="grid grid-cols-2 gap-2 text-white">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2">
                <div className="flex items-center gap-1.5 text-xs text-white/70 mb-1">
                  <Thermometer className="w-3 h-3" />
                  Feels like
                </div>
                <div className="text-lg font-bold">{Math.round(weather.feels_like)}°C</div>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2">
                <div className="flex items-center gap-1.5 text-xs text-white/70 mb-1">
                  <Droplets className="w-3 h-3" />
                  Humidity
                </div>
                <div className="text-lg font-bold">{weather.humidity}%</div>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2">
                <div className="flex items-center gap-1.5 text-xs text-white/70 mb-1">
                  <Wind className="w-3 h-3" />
                  Wind
                </div>
                <div className="text-lg font-bold">{Math.round(weather.wind_speed)} km/h</div>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2">
                <div className="flex items-center gap-1.5 text-xs text-white/70 mb-1">
                  <CloudRain className="w-3 h-3" />
                  Rain
                </div>
                <div className="text-lg font-bold">{weather.rain} mm</div>
              </div>
            </div>

            {/* 3-day forecast */}
            {weather.forecast?.time && (
              <div className="mt-3 pt-3 border-t border-white/20">
                <div className="text-xs text-white/80 mb-2 font-semibold uppercase tracking-wider">3-Day Forecast</div>
                <div className="grid grid-cols-3 gap-1">
                  {weather.forecast.time.map((date, i) => (
                    <div key={date} className="bg-white/10 rounded-lg p-2 text-center">
                      <div className="text-[10px] text-white/70">
                        {i === 0 ? 'Today' : new Date(date).toLocaleDateString('en', { weekday: 'short' })}
                      </div>
                      <div className="text-sm font-bold text-white mt-0.5">
                        {Math.round(weather.forecast.temperature_2m_max[i])}°
                      </div>
                      <div className="text-[10px] text-white/70">
                        {Math.round(weather.forecast.temperature_2m_min[i])}°
                      </div>
                      {weather.forecast.precipitation_probability_max?.[i] > 40 && (
                        <div className="text-[10px] text-blue-200 mt-0.5">
                          💧 {weather.forecast.precipitation_probability_max[i]}%
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Rain alert */}
            {isRaining && (
              <div className="mt-3 p-2 bg-blue-500/30 border border-blue-300/50 rounded-lg text-xs text-white">
                <div className="font-bold mb-0.5">☔ Rain Alert</div>
                <div className="text-white/90">Sheltered booths highlighted in blue</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
